import { useEffect, useMemo, useState } from "react";
import { Shield, Save, Check, X as XIcon, Loader, RefreshCw } from "lucide-react";
import { rolePageAccessApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { usePermission } from "@/context/PermissionContext";
import { useAuth } from "@/hooks/useAuth";

interface PageMeta {
  key: string;
  label: string;
  group: string;
}

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  manager: "Manager",
  receptionist: "Receptionist",
  staff: "Staff",
};

export default function RolePermissionsSection() {
  const { showToast } = useToast();
  const { refresh: refreshPermissions } = usePermission();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [roles, setRoles] = useState<string[]>([]);
  const [pages, setPages] = useState<PageMeta[]>([]);
  const [matrix, setMatrix] = useState<Record<string, Record<string, boolean>>>({});
  const [activeRole, setActiveRole] = useState<string>("manager");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res: any = await rolePageAccessApi.index();
      const payload = res.data?.data || res.data;
      const incomingRoles: string[] = payload.roles || [];
      const visibleRoles = isSuperAdmin
        ? incomingRoles
        : incomingRoles.filter(r => r !== "super_admin");
      setRoles(visibleRoles);
      setPages(payload.pages || []);
      setMatrix(payload.matrix || {});
      // If active tab got hidden, switch to first visible
      if (!visibleRoles.includes(activeRole) && visibleRoles.length > 0) {
        setActiveRole(visibleRoles[0]);
      }
      setDirty(false);
    } catch (err: any) {
      showToast(err?.message || "Failed to load permissions", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groupedPages = useMemo(() => {
    const groups: Record<string, PageMeta[]> = {};
    pages.forEach((p) => {
      if (!groups[p.group]) groups[p.group] = [];
      groups[p.group].push(p);
    });
    return groups;
  }, [pages]);

  const toggle = (page: string) => {
    setMatrix((prev) => ({
      ...prev,
      [activeRole]: {
        ...(prev[activeRole] || {}),
        [page]: !(prev[activeRole]?.[page] ?? false),
      },
    }));
    setDirty(true);
  };

  const setAllInGroup = (groupPages: PageMeta[], value: boolean) => {
    setMatrix((prev) => {
      const next = { ...(prev[activeRole] || {}) };
      groupPages.forEach((p) => {
        next[p.key] = value;
      });
      return { ...prev, [activeRole]: next };
    });
    setDirty(true);
  };

  const save = async () => {
    try {
      setSaving(true);
      await rolePageAccessApi.update(activeRole, matrix[activeRole] || {});
      showToast(`Permissions for ${ROLE_LABELS[activeRole] || activeRole} saved`, "success");
      setDirty(false);
      // Refresh own permission cache so the sidebar updates if you edited your own role
      refreshPermissions();
    } catch (err: any) {
      showToast(err?.message || "Failed to save permissions", "error");
    } finally {
      setSaving(false);
    }
  };

  const isAdminRole = activeRole === "super_admin";

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 p-8 flex items-center justify-center">
        <Loader className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-stone-200 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Role Permissions
            </h2>
            <p className="text-sm text-stone-500 mt-1">
              Control which pages each role can access. Changes apply on next page load.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="text-xs px-3 py-2 border border-stone-200 hover:bg-stone-50 rounded-lg flex items-center gap-1 text-stone-600"
            >
              <RefreshCw className="w-3 h-3" />
              Reload
            </button>
            <button
              onClick={save}
              disabled={!dirty || saving || isAdminRole}
              className="text-xs px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-stone-200 disabled:text-stone-400 text-white rounded-lg font-medium flex items-center gap-1"
            >
              {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save changes
            </button>
          </div>
        </div>

        {/* Role tabs */}
        <div className="flex flex-wrap gap-2 border-b border-stone-200 pb-3 mb-4">
          {roles.map((r) => (
            <button
              key={r}
              onClick={() => {
                if (dirty && !confirm("You have unsaved changes. Discard them?")) return;
                setActiveRole(r);
                setDirty(false);
                load();
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeRole === r
                  ? "bg-amber-100 text-amber-800 border border-amber-200"
                  : "bg-stone-50 text-stone-600 hover:bg-stone-100 border border-stone-200"
              }`}
            >
              {ROLE_LABELS[r] || r}
            </button>
          ))}
        </div>

        {isAdminRole && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            {ROLE_LABELS[activeRole]} always has full access — its permissions cannot be edited.
          </div>
        )}

        <div className="space-y-4">
          {Object.entries(groupedPages).map(([groupName, groupPages]) => {
            const groupAllowed = groupPages.filter((p) => matrix[activeRole]?.[p.key]).length;
            const groupTotal = groupPages.length;
            const allOn = groupAllowed === groupTotal;
            const allOff = groupAllowed === 0;
            return (
              <div key={groupName} className="border border-stone-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-stone-700">{groupName}</p>
                    <p className="text-xs text-stone-500">
                      {groupAllowed} of {groupTotal} pages enabled
                    </p>
                  </div>
                  {!isAdminRole && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => setAllInGroup(groupPages, true)}
                        disabled={allOn}
                        className="text-[10px] px-2 py-1 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40 text-emerald-700 rounded"
                      >
                        Allow all
                      </button>
                      <button
                        onClick={() => setAllInGroup(groupPages, false)}
                        disabled={allOff}
                        className="text-[10px] px-2 py-1 bg-red-50 hover:bg-red-100 disabled:opacity-40 text-red-700 rounded"
                      >
                        Block all
                      </button>
                    </div>
                  )}
                </div>
                <div className="divide-y divide-stone-100">
                  {groupPages.map((p) => {
                    const allowed = matrix[activeRole]?.[p.key] ?? false;
                    return (
                      <div
                        key={p.key}
                        className="px-4 py-2.5 flex items-center justify-between hover:bg-stone-50/50"
                      >
                        <div>
                          <p className="text-sm text-stone-800">{p.label}</p>
                          <p className="text-[10px] text-stone-400 font-mono">{p.key}</p>
                        </div>
                        <button
                          onClick={() => !isAdminRole && toggle(p.key)}
                          disabled={isAdminRole}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            (isAdminRole || allowed) ? "bg-emerald-500" : "bg-stone-300"
                          } ${isAdminRole ? "cursor-not-allowed opacity-70" : ""}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              (isAdminRole || allowed) ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                          {(isAdminRole || allowed) ? (
                            <Check className="absolute left-1.5 w-3 h-3 text-white" />
                          ) : (
                            <XIcon className="absolute right-1.5 w-3 h-3 text-stone-500" />
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
